import { apiVersions } from './endpoints';
import { isLocalMode, getLocalRuntimeUrl } from './localRuntimeClient';
import { getAppUrl } from './appClient';
import { useAppStore } from '@/stores/appStore';
import { queryClient } from '@/providers/QueryProvider';
import { getEnvironmentSettings, type EnvironmentSetting } from './environmentSettingsApi';
import { resolveAppSettingReferences, hasAppSettingReferences } from '@/utils/connections';

/**
 * Build the URL for invoking a service-provider runtime endpoint via the Otto data plane.
 *
 * Cloud mode: hits the Otto control plane, which proxies to the runtime under the app:
 *   {logicEndpoint}/automationProjects/{project}/applications/{appName}/{runtimePath}
 * Local mode: hits StandardRuntimeHost directly:
 *   {localRuntimeUrl}/runtime/webhooks/workflow/api/management/{runtimePath}
 *
 * Project + app are read from the app store, mirroring how dynamic services already
 * resolve them. Throws if either is missing in cloud mode.
 *
 * `runtimePath` should NOT start with a leading slash and should NOT include the
 * `/runtime/webhooks/workflow/api/management` prefix — that's added for local mode
 * and stripped in cloud mode (the Otto proxy strips it for us).
 *
 * An `api-version` query param is appended in both modes (Otto's preview version
 * in cloud, the StandardRuntimeHost service-provider version locally).
 */
const LOCAL_SERVICE_PROVIDER_API_VERSION = '2024-02-01';

export const buildServiceProviderRuntimeUrl = (runtimePath: string): string => {
  const sep = runtimePath.includes('?') ? '&' : '?';
  if (isLocalMode()) {
    return `${getLocalRuntimeUrl()}/runtime/webhooks/workflow/api/management/${runtimePath}${sep}api-version=${LOCAL_SERVICE_PROVIDER_API_VERSION}`;
  }
  const projectName = useAppStore.getState().selectedProject ?? '';
  const appName = useAppStore.getState().selectedAppName ?? '';
  if (!projectName || !appName) {
    throw new Error('No project/app selected — cannot route service-provider runtime call through Otto data plane');
  }
  return `${getAppUrl({ projectName, appName })}/${runtimePath}${sep}api-version=${apiVersions.otto}`;
};

/**
 * Build the dynamicInvoke URL for a service-provider operation.
 * Wraps the generic runtime-URL builder with the dynamicInvoke path shape so
 * all dynamic services (list, schema, tree) stay in sync on path + api-version.
 */
export const buildDynamicInvokeUrl = (providerName: string, operationId: string): string =>
  buildServiceProviderRuntimeUrl(
    `operationGroups/${providerName}/operations/${operationId}/dynamicInvoke`
  );

/**
 * Shape of the connection envelope inside a dynamicInvoke body for a
 * service-provider connector. The runtime expects this to mirror the on-disk
 * `connections.json` entry: visible parameters under `parameterValues`, the
 * picked auth set as `parameterSetName`, and the connector identity under
 * `serviceProvider.id`.
 */
export interface ServiceProviderConnectionPayload {
  parameterValues: Record<string, unknown>;
  serviceProvider: { id: string };
  parameterSetName?: string;
}

interface ConnectionReferenceLike {
  connectionProperties?: Record<string, unknown> | undefined;
}

/**
 * Build the `configuration.connection` payload for a service-provider
 * dynamicInvoke call from a `ConnectionReference`.
 *
 * Service-provider connections have no separate ARM resource — the runtime
 * needs the full `parameterValues` / `parameterSetName` / `serviceProvider.id`
 * shape stashed on the reference at create or load time.
 *
 * The portal stores the chosen parameter set as the internal marker
 * `_parameterSetName` inside `connectionProperties`; this helper strips it back
 * out and lifts it to `parameterSetName` at the connection envelope. Callers
 * must rely on this shared helper rather than re-implementing the extraction
 * inline (regression source for issue #371: when even one call site forgets
 * to lift `parameterSetName`, the runtime returns a misleading 500/400).
 */
export function buildServiceProviderConnectionPayload(
  connectorId: string,
  connectionReference: ConnectionReferenceLike,
  appSettings?: Record<string, string>,
): ServiceProviderConnectionPayload {
  const rawProps = (connectionReference.connectionProperties ?? {}) as Record<string, unknown>;
  const { _parameterSetName, ...parameterValues } = rawProps;

  // dynamicInvoke does not evaluate `@appsetting()` — only the execution engine does.
  const resolved = appSettings
    ? resolveAppSettingReferences(parameterValues, appSettings)
    : parameterValues;

  const payload: ServiceProviderConnectionPayload = {
    parameterValues: resolved,
    serviceProvider: { id: connectorId },
  };
  if (typeof _parameterSetName === 'string' && _parameterSetName.length > 0) {
    payload.parameterSetName = _parameterSetName;
  }
  return payload;
}

/**
 * Read app settings as a `name → value` map from the React Query cache
 * (populated by `useEnvironmentSettings`), falling back to a network fetch
 * and seeding the cache. Returns `{}` when no app context is selected.
 */
async function loadAppSettingsMap(token: string, isDraftMode: boolean): Promise<Record<string, string>> {
  const { selectedProject, selectedAppName } = useAppStore.getState();
  if (!selectedProject || !selectedAppName) return {};

  // Draft and published apps have distinct env-settings; the caller passes the
  // current designer mode (threaded from the hook whose fetch key includes it)
  // so we hit the same cache entry as `useEnvironmentSettings`, which is also
  // keyed on `isDraftMode`.
  const queryKey = ['environmentSettings', selectedProject, selectedAppName, isDraftMode] as const;
  let settings = queryClient.getQueryData<EnvironmentSetting[]>(queryKey);
  if (!settings) {
    settings = await getEnvironmentSettings(
      selectedProject,
      selectedAppName,
      isDraftMode,
      isLocalMode() ? '' : token,
    );
    queryClient.setQueryData(queryKey, settings);
  }

  const map: Record<string, string> = {};
  for (const s of settings) {
    if (typeof s.value === 'string') map[s.name] = s.value;
  }
  return map;
}

/**
 * Async variant that loads the current app's settings and inlines
 * `@appsetting('NAME')` references. Use from dynamicInvoke callers — the
 * runtime endpoint takes literal values, not workflow expressions.
 *
 * Skips the env-settings load entirely when the reference has no
 * `@appsetting()` leaves, so connections with only inline values don't pay
 * the network/cache hit (and aren't broken by a transient env-settings API
 * failure that's irrelevant to them).
 */
export async function buildResolvedServiceProviderConnectionPayload(
  connectorId: string,
  connectionReference: ConnectionReferenceLike,
  token: string,
  isDraftMode: boolean,
): Promise<ServiceProviderConnectionPayload> {
  const props = connectionReference.connectionProperties;
  const needsResolution = props ? hasAppSettingReferences(props) : false;
  const appSettings = needsResolution ? await loadAppSettingsMap(token, isDraftMode) : undefined;
  return buildServiceProviderConnectionPayload(connectorId, connectionReference, appSettings);
}
